import React from 'react'
import { useTheme } from '../../../components/hooks/useTheme';
import { Divider, FormControlLabel, Radio, RadioGroup } from '@mui/material';

type DistanceSettingsProps = {
    chargeDistanceWhenLimitIsZero: boolean;
    setChargeDistanceWhenLimitIsZero: (value: boolean) => void;
    totalDistanceLimit: number;
    setTotalDistanceLimit: (value: number) => void;
    distanceLimitPerDay: number;
    setDistanceLimitPerDay: (value: number) => void;
    distanceLimitPerWeek: number;
    setDistanceLimitPerWeek: (value: number) => void;
    distanceLimitPerMonth: number;
    setDistanceLimitPerMonth: (value: number) => void;
}

const DistanceSettings = ({
    chargeDistanceWhenLimitIsZero,
    setChargeDistanceWhenLimitIsZero,
    totalDistanceLimit,
    setTotalDistanceLimit,
    distanceLimitPerDay,
    setDistanceLimitPerDay,
    distanceLimitPerWeek,
    setDistanceLimitPerWeek,
    distanceLimitPerMonth,
    setDistanceLimitPerMonth
}: DistanceSettingsProps) => {
    const { themeStyles } = useTheme();

    return (
        <div className="mt-[1.5rem]">
            <span className={`text-[14px] font-semibold inline-block mb-2 ${themeStyles.main.textHeadingColor}`}>
                Distance Settings
            </span>

            <Divider className={`w-full ${themeStyles.divider.bg}`} />

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-[1rem]">
                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Charge Distance when Limit is Zero</label>
                    <RadioGroup
                        row
                        value={chargeDistanceWhenLimitIsZero ? "Yes" : "No"}
                        onChange={(e) => setChargeDistanceWhenLimitIsZero(e.target.value === "Yes")}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}

                                />
                            }
                            label="Yes"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />

                        <FormControlLabel
                            value="No"
                            control={
                                <Radio
                                    sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                            color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            }
                            label="No"
                            sx={{
                                color: themeStyles.form.labelTextColor
                            }}
                        />
                    </RadioGroup>
                </div>

                <div />

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Distance Limit per Hour</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={totalDistanceLimit}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setTotalDistanceLimit(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Distance Limit per Day</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={distanceLimitPerDay}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setDistanceLimitPerDay(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Distance Limit per Week</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={distanceLimitPerWeek}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setDistanceLimitPerWeek(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Distance Limit per Month</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className={`w-full px-[8px] py-[6px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                        value={distanceLimitPerMonth}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setDistanceLimitPerMonth(value === "" ? 0 : Number(value));
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                                "Home",
                                "End",
                            ];

                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text");

                            if (!/^\d+$/.test(pasted)) {
                                e.preventDefault();
                            }
                        }}
                        onDrop={(e) => e.preventDefault()}
                        autoComplete="off"
                    />
                </div>
            </div>
        </div>
    )
}

export default DistanceSettings